/**
 * DropPageHero — the hero switch for a PAGE-style Drop, by POV and only by POV.
 *
 * Extracted out of `DropPageSurface` to keep that file under the 400-line
 * ceiling — this was its single largest self-contained block.
 *
 *   PUBLISHER-family → PublisherHero. The title or the org IS the page.
 *   CREATOR          → DropHero, identity-led.
 *   Legacy, no POV   → the hub hero (75/25 featured + games).
 *
 * Release status is NOT part of this decision. A pre-launch title has a
 * countdown, a trailer and a pre-order; a shipped one does not. That is
 * dynamic state INSIDE the publisher hero, not a different kind of page —
 * treating it as a layout switch is what made a Drop created as Publisher
 * render as a hub until someone filled a release in.
 */

import React from "react";
import {DropHero} from "@/widgets/DropHero";
import {PublisherHero} from "@/widgets/PublisherHero";
import {PublisherModesRail} from "@/widgets/PublisherModesRail";
import {Hero} from "@/widgets/Hero";

export interface DropPageHeroProps {
    drop: any;
    layout: string;
    hasLiveStreams: boolean;
    pulse: any;
    handleLoginRequired: () => void;
    handleInviteClick: () => void;
    referralUserId?: string;
    discordOnlineCount?: number;
}

export const DropPageHero: React.FC<DropPageHeroProps> = ({
    drop, layout, hasLiveStreams, pulse, handleLoginRequired, handleInviteClick, referralUserId, discordOnlineCount,
}) =>
    layout === "PUBLISHER"
        ? <>
            <PublisherHero
                /*
                 * No launch attached is the ORDINARY case — a
                 * franchise, a shipped title, a publisher. The
                 * hero renders in full either way; a release
                 * only layers a pill, a date, a countdown and
                 * the CTA's wording on top of it.
                 */
                release={drop.release ?? {}}
                brandName={drop.name}
                /*
                 * The authored credits line, NOT `description`.
                 *
                 * `description` is whatever the social parser
                 * imported from the connected channel — on MW4
                 * a three-sentence bio, rendered into a slot
                 * styled for about six words of 10px mono. It
                 * filled the space under the title with grey
                 * body copy on every Drop created from a URL.
                 */
                creditLine={drop.creditLine}
                parentName={drop.parentDropName}
                parentHref={drop.parentDropSlug ? `/drop-in/${drop.parentDropSlug}` : undefined}
                markUrl={drop.creatorProfileImageUrl}
                /*
                 * The wide wordmark, not the square icon —
                 * see PublisherHeroProps.logoImageUrl.
                 */
                logoImageUrl={drop.logoImageUrl}
                /*
                 * The ONE hero image, resolved the same way
                 * Hero.tsx does for every other layout.
                 */
                heroImageUrl={drop.heroSection?.heroImageUrl ||
                                    drop.heroSection?.heroImageExternalUrl ||
                                    drop.creatorHeaderImageUrl}
                /*
                 * The hero image doubles as the trailer poster —
                 * one artwork, not a separate upload.
                 */
                trailerPosterUrl={drop.heroSection?.heroImageUrl ||
                                    drop.creatorHeaderImageUrl}
                /*
                 * All of this is the Drop's, not the launch's —
                 * a shipped game and a franchise have every one
                 * of them. See
                 * knowledge/projects/active/drop-identity-collapse.
                 */
                purchaseUrl={drop.purchaseUrl}
                trailerUrl={drop.trailerUrl}
                videos={drop.videos}
                tagline={drop.tagline}
                synopsis={drop.synopsis}
                platforms={drop.platforms}
                verified={drop.verified}
                socials={drop.socials}
                followSlug={drop.slug}
                /*
                 * The editor's CTA colour fields, which this
                 * hero previously ignored — setting them did
                 * nothing because they belong to the generic
                 * hero CTA and this is a different button.
                 */
                ctaBackground={drop.heroSection?.ctaBackgroundColor}
                ctaTextColor={drop.heroSection?.ctaTextColor}
                onLoginRequired={handleLoginRequired}
                referralUserId={referralUserId}
            />
            {/*
                              * The rail is the DROP's. Pre-launch it holds
                              * modes and beta milestones; after launch the same
                              * rail carries the season cadence — which is why a
                              * franchise with no release at all still has one.
                              */}
            <PublisherModesRail items={drop.modes ?? []} />
        </>
        : layout === "CREATOR"
        /*
         * When the creator is live the full hero is suppressed: the
         * stream is promoted to the top of CreatorSection and the
         * creator identity relocates directly beneath it (rendered
         * there as a compact DropHero in identityOnly mode). Offline,
         * the full identity-led hero renders here as before.
         */
            ? hasLiveStreams
                ? null
                : <DropHero
                    dropName={drop.name}
                    dropSlug={drop.slug}
                    dropId={drop.id}
                    themeColors={drop.themeColors}
                    socials={drop.socials}
                    storefronts={(drop as { storefronts?: Parameters<typeof DropHero>[0]["storefronts"] }).storefronts}
                    creatorHeaderImageUrl={drop.creatorHeaderImageUrl}
                    creatorProfileImageUrl={drop.creatorProfileImageUrl}
                    followerCount={pulse?.followerCount ?? drop.followerCount}
                    isLive={hasLiveStreams}
                    joinedAt={drop.createdAt}
                    followMode={drop.followMode}
                    onInviteClick={handleInviteClick}
                    onLoginRequired={handleLoginRequired}
                    referralUserId={referralUserId}
                />
            : <Hero
                heroSection={drop.heroSection}
                hubName={drop.name}
                surfaceSlug={drop.slug}
                dropSlug={drop.slug}
                surfaceId={drop.id}
                themeColors={drop.themeColors}
                games={(drop.derivedGames || [])}
                socials={(drop.socials || [])}
                onGameClick={() => {}}
                creatorHeaderImageUrl={drop.creatorHeaderImageUrl}
                creatorProfileImageUrl={drop.creatorProfileImageUrl}
                showCtaButton={Boolean(drop.heroSection?.ctaText)}
                hubType={drop.dropType}
                hubDescription={drop.description}
                followerCount={pulse?.followerCount ?? drop.followerCount}
                onLoginRequired={handleLoginRequired}
                referralUserId={referralUserId}
                onInviteClick={handleInviteClick}
                heroParallax={drop.themeConfig?.heroParallax}
                isCompact={hasLiveStreams}
                hasLiveStream={hasLiveStreams}
                discordOnlineCount={discordOnlineCount}
            />;
export default DropPageHero;
